/**
 * Demo data for the front-end-only flow. Every value here is what the mock
 * service returns; the real service replaces it (see docs/HANDOFF.md).
 */
import {
   IAreaCode,
   ICampaignRow,
   IFilterRow,
   IOverviewStats,
   IOrgContext,
   IResults,
   IPhoneNumberOption,
   IPlaybook,
   ISampleLead,
   IStarter,
   ITeammate,
   ITouch,
   IVoiceOption,
   TCadencePreset,
   TCampaignKind,
   TPeriod,
   TLeadType,
   TPlaybookContent,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'

export const ORG: IOrgContext = {
   organization: 'Patel Realty',
   agentName: 'Sam',
   ownerFirstName: 'Sam',
   phoneNumber: '(719) 555-0140',
}

export const SAMPLE_LEAD: ISampleLead = {
   firstName: 'Jordan',
   city: 'Colorado Springs',
   source: 'Zillow',
}

export const STARTERS: IStarter[] = [
   {
      id: 'new-zillow',
      title: 'New Zillow leads',
      blurb: 'Call every new Zillow lead within 2 minutes of arrival and book the appointment for you.',
      tone: 'purple',
      icon: 'zap',
      description: 'Call my new Zillow buyer leads',
      matchCount: 84,
   },
   {
      id: 'revive-cold',
      title: 'Revive cold leads',
      blurb: 'Work the older leads that never booked, a few each day, and hand you the ones that are ready again.',
      tone: 'pink',
      icon: 'clock',
      description: 'Call my leads older than 30 days that never booked',
      matchCount: 2170,
   },
   {
      id: 'buyer-pond',
      title: 'Buyer Pond leads',
      blurb: 'Call the leads sitting in your Buyer Pond that nobody has claimed yet.',
      tone: 'green',
      icon: 'users',
      description: 'Call the leads in my Buyer Pond',
      matchCount: 312,
   },
]

/** Vocabulary for "Add a filter" (the real service reads FUB sources, stages, tags and ponds). */
/** Values for each filter field (the real app reads them from Follow Up Boss via useLeadOptions). */
export const FILTER_VOCABULARY: Record<
   IFilterRow['field'],
   { value: string; label: string }[]
> = {
   source: [
      'Zillow',
      'Realtor.com',
      'Homes.com',
      'Facebook',
      'Website',
      'Open house',
      'Referral',
   ].map((v) => ({ value: v, label: v })),
   stage: [
      'New lead',
      'Attempted contact',
      'Nurture',
      'Hot',
      'Cold',
      'Past client',
   ].map((v) => ({ value: v, label: v })),
   tags: [
      'buyer',
      'first-time buyer',
      'relocation',
      'investor',
      'pre-approved',
      'do not call',
   ].map((v) => ({ value: v, label: v })),
   assigned_pond_id: [
      { value: '11', label: 'Buyer Pond' },
      { value: '12', label: 'Seller Pond' },
      { value: '13', label: 'Old leads' },
   ],
   inquired_property: [
      '1840 N Cascade Ave',
      '512 E Uintah St',
      '77 Briargate Pkwy',
      '2301 W Colorado Ave',
   ].map((v) => ({ value: v, label: v })),
}

const t = (
   id: string,
   action: ITouch['action'],
   day: number,
   time: string,
   voicemail?: boolean
): ITouch => ({
   id,
   action,
   day,
   time,
   ...(action === 'call' ? { voicemail: !!voicemail } : {}),
})

export const CADENCE_PRESETS: Record<
   Exclude<TCadencePreset, 'custom'>,
   { label: string; blurb: string; touches: ITouch[] }
> = {
   light: {
      label: 'Light touch',
      blurb: '3 touches over 3 days. For leads who asked to be left mostly alone.',
      touches: [
         t('c1', 'call', 1, '09:00', true),
         t('s1', 'text', 1, '09:30'),
         t('s2', 'text', 3, '10:00'),
      ],
   },
   balanced: {
      label: 'Balanced',
      blurb: '5 touches over 3 days. 2 calls with voicemail, 2 texts.',
      touches: [
         t('c1', 'call', 1, '09:00', true),
         t('s1', 'text', 1, '09:30'),
         t('c2', 'call', 2, '10:00', false),
         t('s2', 'text', 3, '10:00'),
      ],
   },
   nurture: {
      label: 'Slow nurture',
      blurb: '5 touches over 3 weeks. For cold leads who need space.',
      touches: [
         t('s1', 'text', 1, '10:00'),
         t('c1', 'call', 2, '10:00', true),
         t('s2', 'text', 7, '11:00'),
         t('c2', 'call', 14, '10:00', true),
         t('s3', 'text', 21, '11:00'),
      ],
   },
   persistent: {
      label: 'Persistent',
      blurb: '9 touches over 10 days. For high-intent sources.',
      touches: [
         t('c1', 'call', 1, '09:00', true),
         t('s1', 'text', 1, '09:30'),
         t('c2', 'call', 2, '10:00', true),
         t('s2', 'text', 3, '10:00'),
         t('c3', 'call', 4, '16:00', false),
         t('s3', 'text', 5, '11:00'),
         t('c4', 'call', 7, '10:00', true),
         t('s4', 'text', 10, '10:00'),
      ],
   },
}

/** The timing each kind of lead starts with: fast for new leads, spaced out for cold ones, short for activity. */
export const KIND_PRESET: Record<
   TCampaignKind,
   Exclude<TCadencePreset, 'custom'>
> = {
   'new-lead': 'persistent',
   revive: 'nurture',
   activity: 'light',
}

export const RECOMMENDED_PLAYBOOK: IPlaybook = {
   origin: 'recommended',
   scriptName: 'Buyer outbound · Recommended v3',
   messageGroupName: 'Warm follow-up',
   preset: 'balanced',
   touches: CADENCE_PRESETS.balanced.touches,
   script: [
      {
         id: 'intro',
         title: 'Introduction',
         text: "Hi {first name}, this is {assistant name} with {organization}. I saw you were looking at homes near {city}. Do you have a quick minute? I'll keep this brief.",
      },
      {
         id: 'qualify',
         title: 'Qualifying questions',
         text: 'Two quick questions. What is your timeline, are you hoping to move in the next few months or just exploring? And what budget range should I keep in mind?',
      },
      {
         id: 'close',
         title: 'Close',
         text: 'I have a few off-market homes that fit. Would you have 15 minutes this week so {agent name} can walk you through them? Tuesday 2pm or Wednesday 11am are open.',
      },
   ],
   texts: [
      {
         id: 's1',
         text: 'Hi {first name}, this is {assistant name} with {organization}. Just tried calling about the homes near {city}. Is texting easier for you?',
      },
      {
         id: 's2',
         text: 'Quick one {first name}: are you hoping to move in the next 3 to 6 months, or just keeping an eye on things?',
      },
   ],
   voicemails: [
      {
         id: 'v1',
         text: "Hi {first name}, it's {assistant name} with {organization}. I'll text you so you have my number. Talk soon.",
      },
   ],
   replyInstructions:
      "If they ask who this is, say it's {assistant name} with {organization}. If they want listings, promise the three best matches and ask for must-haves. If they ask for a call, call now.",
   usedByTeams: 312,
}

/** Recommended words per lead type. Buyers use the playbook above. */
export const RECOMMENDED_BY_TYPE: Record<TLeadType, TPlaybookContent> = {
   buyer: {
      origin: 'recommended',
      scriptId: 'buyer-rec',
      messageGroupId: 'buyer-msg-rec',
      scriptName: RECOMMENDED_PLAYBOOK.scriptName,
      messageGroupName: RECOMMENDED_PLAYBOOK.messageGroupName,
      script: RECOMMENDED_PLAYBOOK.script,
      texts: RECOMMENDED_PLAYBOOK.texts,
      voicemails: RECOMMENDED_PLAYBOOK.voicemails,
      replyInstructions: RECOMMENDED_PLAYBOOK.replyInstructions,
   },
   seller: {
      origin: 'recommended',
      scriptId: 'seller-rec',
      messageGroupId: 'seller-msg-rec',
      scriptName: 'Seller outbound · Recommended v2',
      messageGroupName: 'Seller follow-up',
      script: [
         {
            id: 'intro',
            title: 'Introduction',
            text: 'Hi {first name}, this is {assistant name} with {organization}. You asked about what your home near {city} might sell for. Do you have a quick minute?',
         },
         {
            id: 'qualify',
            title: 'Qualifying questions',
            text: 'Are you thinking of selling in the next few months, or just curious about the value? And have you already bought your next place?',
         },
         {
            id: 'close',
            title: 'Close',
            text: '{agent name} can put together a free value report and walk you through it in 15 minutes. Would Tuesday 2pm or Wednesday 11am work?',
         },
      ],
      texts: [
         {
            id: 's1',
            text: 'Hi {first name}, this is {assistant name} with {organization}. Just tried calling about your home value near {city}. Is texting easier?',
         },
         {
            id: 's2',
            text: 'Quick one {first name}: homes near {city} are moving fast right now. Want a free value report for yours?',
         },
      ],
      voicemails: [
         {
            id: 'v1',
            text: "Hi {first name}, it's {assistant name} with {organization} about your home near {city}. I'll text you so you have my number.",
         },
      ],
      replyInstructions:
         "If they ask who this is, say it's {assistant name} with {organization}. If they want a value, ask for the address and offer a free report. If they ask for a call, call now.",
   },
   rental: {
      origin: 'recommended',
      scriptId: 'rental-rec',
      messageGroupId: 'rental-msg-rec',
      scriptName: 'Renter outbound · Recommended v1',
      messageGroupName: 'Renter follow-up',
      script: [
         {
            id: 'intro',
            title: 'Introduction',
            text: 'Hi {first name}, this is {assistant name} with {organization}. I saw you were looking at rentals near {city}. Do you have a quick minute?',
         },
         {
            id: 'qualify',
            title: 'Qualifying questions',
            text: 'When does your current lease end? And have you thought about buying instead of renting, if the monthly payment were similar?',
         },
         {
            id: 'close',
            title: 'Close',
            text: '{agent name} can show you a few options in 15 minutes, to rent or to buy. Would Tuesday 2pm or Wednesday 11am work?',
         },
      ],
      texts: [
         {
            id: 's1',
            text: 'Hi {first name}, this is {assistant name} with {organization}. Just tried calling about rentals near {city}. Is texting easier?',
         },
         {
            id: 's2',
            text: 'Quick one {first name}: when does your lease end? I can send a few places that fit.',
         },
      ],
      voicemails: [
         {
            id: 'v1',
            text: "Hi {first name}, it's {assistant name} with {organization} about rentals near {city}. I'll text you so you have my number.",
         },
      ],
      replyInstructions:
         "If they ask who this is, say it's {assistant name} with {organization}. If they want listings, ask for move-in date and budget. If they ask for a call, call now.",
   },
}

/** A call script or outbound content group in the demo library. */
export interface IDemoContent {
   id: string
   type: TLeadType
   slot: 'script' | 'messages'
   name: string
   owner: 'recommended' | 'yours'
   content: Partial<TPlaybookContent>
}

const sec = (id: string, title: string, text: string) => ({ id, title, text })
const msg = (id: string, text: string) => ({ id, text })

/** What the demo organization can pick from: Forward Flow's recommended items and its own earlier ones. */
export const CONTENT_LIBRARY: IDemoContent[] = [
   ...(['buyer', 'seller', 'rental'] as TLeadType[]).flatMap(
      (t): IDemoContent[] => [
         {
            id: `${t}-rec`,
            type: t,
            slot: 'script',
            name: RECOMMENDED_BY_TYPE[t].scriptName,
            owner: 'recommended',
            content: { script: RECOMMENDED_BY_TYPE[t].script },
         },
         {
            id: `${t}-msg-rec`,
            type: t,
            slot: 'messages',
            name: RECOMMENDED_BY_TYPE[t].messageGroupName,
            owner: 'recommended',
            content: {
               texts: RECOMMENDED_BY_TYPE[t].texts,
               voicemails: RECOMMENDED_BY_TYPE[t].voicemails,
            },
         },
      ]
   ),
   {
      id: 'buyer-short',
      type: 'buyer',
      slot: 'script',
      name: 'Buyer outbound · Short and direct',
      owner: 'recommended',
      content: {
         script: [
            sec(
               'intro',
               'Introduction',
               'Hi {first name}, {assistant name} with {organization}. You were looking at homes near {city}. Still looking?'
            ),
            sec(
               'close',
               'Close',
               'Great. {agent name} has 15 minutes Tuesday or Wednesday to walk you through a few that fit. Which works?'
            ),
         ],
      },
   },
   {
      id: 'buyer-openhouse',
      type: 'buyer',
      slot: 'script',
      name: 'Open house follow-up',
      owner: 'yours',
      content: {
         script: [
            sec(
               'intro',
               'Introduction',
               'Hi {first name}, it is {assistant name} with {organization}. Thanks for stopping by the open house near {city} last weekend.'
            ),
            sec(
               'qualify',
               'What they thought',
               'What did you think of the place? Was it close to what you are looking for?'
            ),
            sec(
               'close',
               'Close',
               'If you like, {agent name} can line up two or three similar homes this week. Would Thursday evening work?'
            ),
         ],
      },
   },
   {
      id: 'buyer-msg-nudge',
      type: 'buyer',
      slot: 'messages',
      name: 'Persistent nudge',
      owner: 'recommended',
      content: {
         texts: [
            msg(
               's1',
               'Hi {first name}, {assistant name} from {organization}. Quick question about your home search near {city}?'
            ),
            msg(
               's2',
               'Still looking, {first name}? Two new listings near {city} just came up.'
            ),
            msg(
               's3',
               'Last one from me {first name}: want me to send the best three matches?'
            ),
         ],
         voicemails: [
            msg(
               'v1',
               "Hi {first name}, {assistant name} with {organization}. I'll text you the listings. Talk soon."
            ),
         ],
      },
   },
   {
      id: 'buyer-msg-spring',
      type: 'buyer',
      slot: 'messages',
      name: 'Spring market texts',
      owner: 'yours',
      content: {
         texts: [
            msg(
               's1',
               'Hi {first name}! Spring is busy near {city}. Want first look at new listings before they hit Zillow?'
            ),
            msg(
               's2',
               '{first name}, three homes in your range came up this week. Should I send them?'
            ),
         ],
         voicemails: [
            msg(
               'v1',
               "Hi {first name}, it's {assistant name}. A few spring listings near {city} fit you. I'll text them over."
            ),
         ],
      },
   },
   {
      id: 'seller-cma',
      type: 'seller',
      slot: 'script',
      name: 'Seller · Home value first',
      owner: 'recommended',
      content: {
         script: [
            sec(
               'intro',
               'Introduction',
               'Hi {first name}, {assistant name} with {organization}. Homes near {city} sold above asking this month. Curious what yours is worth?'
            ),
            sec(
               'close',
               'Close',
               '{agent name} can bring a free value report by this week. Tuesday or Wednesday?'
            ),
         ],
      },
   },
   {
      id: 'seller-expired',
      type: 'seller',
      slot: 'script',
      name: 'Expired listings',
      owner: 'yours',
      content: {
         script: [
            sec(
               'intro',
               'Introduction',
               'Hi {first name}, this is {assistant name} with {organization}. I saw your home near {city} came off the market. Still hoping to sell?'
            ),
            sec(
               'qualify',
               'What happened',
               'What do you think held it back: the price, the photos, or the timing?'
            ),
            sec(
               'close',
               'Close',
               '{agent name} has a plan for homes like yours. Could we show you in 15 minutes this week?'
            ),
         ],
      },
   },
   {
      id: 'seller-msg-cma',
      type: 'seller',
      slot: 'messages',
      name: 'Value report follow-up',
      owner: 'yours',
      content: {
         texts: [
            msg(
               's1',
               'Hi {first name}, your free value report for your home near {city} is ready. Want it by text or email?'
            ),
            msg(
               's2',
               'Two homes on your street sold this month, {first name}. Happy to share what they went for.'
            ),
         ],
         voicemails: [
            msg(
               'v1',
               "Hi {first name}, {assistant name} here. Your home value report is ready. I'll text it over."
            ),
         ],
      },
   },
   {
      id: 'rental-lease',
      type: 'rental',
      slot: 'script',
      name: 'Lease ending soon',
      owner: 'yours',
      content: {
         script: [
            sec(
               'intro',
               'Introduction',
               'Hi {first name}, {assistant name} with {organization}. Is your lease near {city} ending in the next few months?'
            ),
            sec(
               'close',
               'Close',
               '{agent name} can show you options to rent or buy. Would a 15 minute call this week help?'
            ),
         ],
      },
   },
]

/**
 * The current public voices (GET /svc/campaigns/voices/public, newest first:
 * Aoede, Sarah, Jonathan, Erin). Appointment rates are demo numbers.
 */
export const VOICES: IVoiceOption[] = [
   {
      id: 'Kore',
      defaultName: 'Erin',
      style: 'Warm & clear',
      hue: 262,
      appointmentRate: 6.8,
      bestPerforming: true,
      previewUrl:
         'https://forward-flow-assets.s3.us-west-2.amazonaws.com/recordings/Erin+FF+Demo.wav',
   },
   {
      id: 'Aoede',
      defaultName: 'Aoede',
      style: 'Breezy Approachable',
      hue: 165,
      appointmentRate: 6.1,
      isNew: true,
   },
   {
      id: 'Algenib',
      defaultName: 'Jonathan',
      style: 'Calm & steady',
      hue: 212,
      appointmentRate: 5.4,
      previewUrl:
         'https://forward-flow-assets.s3.us-west-2.amazonaws.com/recordings/Jonathan+FF+Demo.wav',
   },
   {
      id: 'Sulafat',
      defaultName: 'Sarah',
      style: 'Friendly & upbeat',
      hue: 24,
      appointmentRate: 5.2,
      previewUrl:
         'https://forward-flow-assets.s3.us-west-2.amazonaws.com/recordings/First+Bridge+Sarah+-+Sulafat.wav',
   },
]

export const AREA_CODES: IAreaCode[] = [
   { code: '719', city: 'Colorado Springs', leadShare: 62 },
   { code: '303', city: 'Denver', leadShare: 21 },
   { code: '720', city: 'Denver metro', leadShare: 9 },
   { code: '970', city: 'Fort Collins', leadShare: 5 },
]

const pool = (
   areaCode: string,
   city: string,
   lines: string[]
): IPhoneNumberOption[] =>
   lines.map((l) => ({ number: `(${areaCode}) 555-${l}`, areaCode, city }))
export const NUMBER_POOL: IPhoneNumberOption[] = [
   ...pool('719', 'Colorado Springs', ['0140', '0193', '0127', '0171']),
   ...pool('303', 'Denver', ['0117', '0158', '0134']),
   ...pool('720', 'Denver metro', ['0162', '0119']),
   ...pool('970', 'Fort Collins', ['0108', '0185']),
   ...pool('719', 'Pueblo', ['0146']),
]

export const OWNED_NUMBERS: IPhoneNumberOption[] = [
   {
      number: '(719) 555-0101',
      areaCode: '719',
      city: 'Colorado Springs',
      owned: true,
   },
]

export const TEAMMATES: ITeammate[] = [
   { id: 11, name: 'Sam Patel', initials: 'SP', role: 'Owner' },
   { id: 12, name: 'Maya Chen', initials: 'MC', role: 'Agent' },
   { id: 13, name: 'Luis Ortega', initials: 'LO', role: 'Agent' },
]

export const CAMPAIGNS: ICampaignRow[] = [
   {
      id: 'c-realtor-sellers',
      kind: 'new-lead',
      name: 'Realtor.com sellers',
      source: 'Realtor.com',
      status: 'live',
      voiceHue: 262,
      voiceName: 'Erin',
      leads: 612,
      reached: 388,
      appointments: 41,
      transfers: 17,
      trend: [2, 3, 2, 4, 3, 5, 4, 3, 5, 4, 6, 5],
      lastActivity: '4 min ago',
   },
   {
      id: 'c-zillow-buyers',
      kind: 'new-lead',
      name: 'Zillow buyers',
      source: 'Zillow',
      status: 'live',
      voiceHue: 165,
      voiceName: 'Aoede',
      leads: 1240,
      reached: 702,
      appointments: 58,
      transfers: 24,
      trend: [3, 4, 4, 5, 4, 6, 5, 7, 6, 6, 8, 7],
      lastActivity: '12 min ago',
   },
   {
      id: 'c-cold-revive',
      kind: 'revive',
      name: 'Revive cold leads',
      source: 'All sources',
      status: 'live',
      voiceHue: 212,
      voiceName: 'Jonathan',
      leads: 2170,
      reached: 611,
      appointments: 22,
      transfers: 6,
      trend: [1, 2, 1, 2, 2, 1, 3, 2, 2, 3, 2, 3],
      lastActivity: '1 hr ago',
   },
   {
      id: 'c-open-house',
      kind: 'activity',
      name: 'Spring open house list',
      source: 'Open house',
      status: 'paused',
      voiceHue: 196,
      voiceName: 'Sarah',
      leads: 240,
      reached: 151,
      appointments: 9,
      transfers: 3,
      trend: [2, 2, 1, 2, 1, 1, 0, 0, 0, 0, 0, 0],
      lastActivity: '6 days ago',
   },
   {
      id: 'c-buyer-pond',
      kind: 'revive',
      name: 'Buyer Pond',
      source: 'Buyer Pond',
      status: 'draft',
      voiceHue: 24,
      voiceName: 'Erin',
      leads: 312,
      reached: 0,
      appointments: 0,
      transfers: 0,
      trend: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      lastActivity: 'Not started',
   },
]

export const NUMBER_PRICE_PER_MONTH = 2

/** Overview per period. Values scale with the period; previous is the period before. */
/**
 * Demo results for a period: a believable 12-point trend that ends at
 * `value`, with `previous` for the period before. `scale` sizes one campaign
 * against the organization (1).
 */
export const demoResults = (period: TPeriod, scale = 1): IResults => {
   const days = PERIOD_DAYS_DEMO[period]
   const lifetime = period === 'all'
   const m = (perDay: number, growth: number) => {
      const total = perDay * days * scale
      /* Small totals keep a decimal per point, so the line is not flat steps. */
      const decimals = total / 12 < 50 ? 1 : 0
      const trend = Array.from({ length: 12 }, (_, i) =>
         Number(
            ((total / 12) * (1 + growth * ((i - 5.5) / 11))).toFixed(decimals)
         )
      )
      return {
         value: Number(total.toFixed(decimals)),
         previous: lifetime
            ? 0
            : Number((total / (1 + growth)).toFixed(decimals)),
         trend,
      }
   }
   const pct = (value: number, growth: number) => ({
      value,
      previous: lifetime ? 0 : Number((value / (1 + growth)).toFixed(1)),
      trend: Array.from({ length: 12 }, (_, i) =>
         Number((value * (1 + growth * ((i - 5.5) / 11))).toFixed(1))
      ),
   })
   return {
      period,
      leads: m(134, 0.11),
      conversations: m(39.6, 0.17),
      appointments: m(4.3, 0.17),
      transfers: m(1.6, 0.29),
      attemptsPerLead: { ...pct(3.3, -0.03), value: 3.3 },
      callsAnswered: pct(34, 0.06),
      textsReplied: pct(17, 0.09),
   }
}
const PERIOD_DAYS_DEMO: Record<TPeriod, number> = {
   '7d': 7,
   '30d': 30,
   '90d': 90,
   all: 410,
}

/** Totals above the campaign list, by period. */
export const OVERVIEW: Record<TPeriod, Omit<IOverviewStats, 'period'>> = {
   '7d': { liveCampaigns: 3, ...demoResults('7d') },
   '30d': { liveCampaigns: 3, ...demoResults('30d') },
   '90d': { liveCampaigns: 3, ...demoResults('90d') },
   all: { liveCampaigns: 3, ...demoResults('all') },
}
